import {
    CellValueChangedEvent,
    ColDef,
    ColGroupDef,
    ColumnGroupShowType,
    ColumnMovedEvent,
    FirstDataRenderedEvent,
    GridApi,
    GridReadyEvent,
    ValueGetterParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useGetCards, useUpdateCard } from '../services/auditService';
import {
    cardStatusMap,
    lexiConclusionsMap,
    lexiFeedbackCategoryMap,
    worksheetStatusMap,
} from '../services/boardTransformer';
import { useGetAuditReviews } from '../services/reviewService';
import { titleToKeyMapping } from '../services/standardMappingTransformer';
import { AuditType, TransformedAuditBoard } from '../Types/Audit';
import { Card, Row } from '../Types/Card';
import {
    Review,
    REVIEW_STATUS,
    ReviewStatus,
    reviewStatusStrings,
    ReviewType,
} from '../Types/Review';
import { StandardMapping } from '../Types/Standards';
import GridCellAttachments from './GridCellAttachments';
import GridCellBadge from './GridCellBadge';
import GridCellBadgeList from './GridCellBadgeList';
import GridCellIframeContainer from './GridCellIframeContainer';
import GridCellResult from './GridCellResults';
import GridCellReview from './GridCellReview';
import { BadgeColors } from './ui/Badge';

type ColDefOrGroupDef = ColDef | ColGroupDef;
interface AuditGridProps {
    audit?: TransformedAuditBoard;
    onModelUpdated?: () => void;
    onFirstDataRendered?: () => void;
    onCellValueChanged?: () => void;
}

interface ValueFormatterParams {
    value: Review | null;
}

interface ColumnDefinition {
    headerName: string;
    field: string;
}

const badgeTestStatusColorMapping = (value: string) => {
    if (value === 'Incomplete') {
        return BadgeColors.DEFAULT;
    } else if (value === 'Under Review') {
        return BadgeColors.INFO;
    } else if (value === 'Approved') {
        return BadgeColors.ACCEPTED;
    } else if (value === 'Exceptions') {
        return BadgeColors.ERROR;
    } else if (value === 'Descoped') {
        return BadgeColors.DESCOPED;
    }
};

const testingStatusColorMapping = (value: string) => {
    if (value === 'Control Gap') {
        return BadgeColors.ERROR;
    } else if (value === 'Implemented') {
        return BadgeColors.INFO;
    } else if (value === 'For Review') {
        return BadgeColors.WARNING;
    } else if (value === 'Audit Query') {
        return BadgeColors.INFO;
    } else if (value === 'Accepted') {
        return BadgeColors.ACCEPTED;
    }
};

const lexiFeedbackCategoryColorMapping = (value: string) => {
    if (value === 'AI performed correctly') {
        return BadgeColors.ACCEPTED;
    } else if (value === 'AI \'incomplete\' when it should have \'passed\'') {
        return BadgeColors.WARNING;
    } else if (value === 'AI \'passed\' when it should have been \'incomplete\'') {
        return BadgeColors.ERROR;
    } else if (value === 'Others') {
        return BadgeColors.INFO;
    }
};

const generateStandardColumns = (auditTypes: AuditType[]): ColDefOrGroupDef[] => {
    // Initialize SOC 2 group with soc2All and ensure children is defined
    const columnDefinitions = generateStandardsColumnDefinitions(auditTypes);
    const standards: ColGroupDef = {
        headerName: 'Standards',
        children: [
            {
                headerName: 'All Standards',
                field: 'standardMapping.all',
                cellRenderer: GridCellBadgeList,
                columnGroupShow: 'closed' as ColumnGroupShowType,
                wrapText: true,
            },
            ...columnDefinitions.map(({ headerName, field }) => {
                return {
                    headerName,
                    field,
                    columnGroupShow: 'open' as ColumnGroupShowType,
                    cellRenderer: GridCellBadgeList,
                    wrapText: true,
                    editable: true,
                    cellEditor: 'agSmallTextCellEditor',
                    cellEditorPopup: false,
                    cellDataType: 'array',
                    resizable: true,
                    width: 350,
                };
            }),
        ],
        openByDefault: true,
    };
    return [standards];
};

const keyToTitleMapping: Record<keyof StandardMapping, string> = Object.keys(
    titleToKeyMapping,
).reduce(
    (acc, title) => {
        const key = titleToKeyMapping[title as keyof typeof titleToKeyMapping];
        acc[key] = title;
        return acc;
    },
    {} as Record<keyof StandardMapping, string>,
);

const generateStandardsColumnDefinitions = (auditTypes: string[]): ColumnDefinition[] => {
    const filteredKeys = (
        Object.values(titleToKeyMapping) as Array<keyof StandardMapping>
    ).filter((key) => auditTypes.includes(keyToTitleMapping[key]));

    return filteredKeys.map((key) => ({
        headerName: keyToTitleMapping[key],
        field: `standardMapping.${key}`,
    }));
};

const mergeReviewsOntoCards = (cards: Card[], reviews: Review[]): Row[] => {
    const updatedData = cards.map((card) => {
        const filteredReviews = reviews?.filter(
            (review: Review) => review && review.reviewableId === card.id,
        );

        const reviewsByType = filteredReviews?.reduce(
            (acc: Partial<Record<ReviewType, Review>>, review) => {
                if (review.reviewType && ReviewType[review.reviewType]) {
                    acc[review.reviewType] = review;
                }
                return acc;
            },
            {},
        );

        return {
            ...card,
            ...reviewsByType,
        } as Row;
    });
    return updatedData;
};

const generateAuditStandards = (auditTypes: string[], rows: Row[]): Row[] => {
    const updatedRows = rows.map((card) => {
        const filteredStandardKeys = (
            Object.values(titleToKeyMapping) as Array<keyof StandardMapping>
        ).filter((key) => auditTypes.includes(keyToTitleMapping[key]));

        const filteredCardStandards = Object.entries(card.standardMapping)
            .map(([key, value]) => {
                return filteredStandardKeys.includes(key as keyof StandardMapping)
                    ? value
                    : null;
            })
            .filter(Boolean)
            .flat();
        const standardMapping = {
            standardMapping: {
                ...card.standardMapping,
                all: filteredCardStandards,
            },
        };
        return { ...card, ...standardMapping };
    });
    return updatedRows;
};

const SECTION_OPTIONS = [
    'Control Environment',
    'Information & Communication',
    'Risk Management',
    'Vendor Management',
    'Change Management',
    'System Security',
    'System Operations',
    'Physical Security',
    'Confidentiality',
    'Availability',
    'Processing Integrity',
    'Privacy',
    'Social and Community Impact',
    'Environmental Impact',
];

interface ValueFormatterParams {
    value: Review | null;
}

interface ArrayValueFormatterParams {
    value?: string[] | [];
    newValue?: string;
}

const AuditGrid = forwardRef<{ api: GridApi | null }, AuditGridProps>(
    (
        {
            audit,
            onModelUpdated,
            onFirstDataRendered: onFirstDataRenderedProp,
            onCellValueChanged: onCellValueChangedProp,
        },
        ref,
    ) => {
        const {
            data: cards,
            error: _cardsError,
            isLoading: cardsLoading,
        } = useGetCards(audit?.id);
        const {
            data: reviewData,
            error: _reviewError,
            isLoading: _reviewLoading,
        } = useGetAuditReviews(audit?.id);
        const updateCardMutation = useUpdateCard();
        const [rowData, setRowData] = useState<Row[]>([]);
        const defaultColDef = useMemo<ColDef>(() => {
            return {
                width: 150,
                filter: 'agTextColumnFilter',
                wrapText: true,
                autoHeight: true,
                floatingFilter: true,
                filterParams: {
                    closeOnApply: true,
                    buttons: ['cancel', 'reset', 'apply'],
                },
            };
        }, []);
        const gridApi = useRef<GridApi | null>(null);

        useImperativeHandle(ref, () => ({
            api: gridApi.current,
        }));

        useEffect(() => {
            if (Array.isArray(cards)) {
                let updatedData = mergeReviewsOntoCards(
                    cards as Card[],
                    reviewData || [],
                );
                updatedData = generateAuditStandards(audit?.auditType || [], updatedData);
                setRowData(updatedData);
            }
        }, [cards, reviewData, audit]);

        const dataTypeDefinitions = useMemo(() => {
            return {
                review: {
                    extendsDataType: 'object',
                    baseDataType: 'object' as const,
                    valueFormatter: (params: ValueFormatterParams) =>
                        params.value == null ? '' : params.value.status,
                    dataTypeMatcher: (value: unknown): boolean =>
                        typeof value === 'object' && value !== null && 'status' in value,
                },
                array: {
                    extendsDataType: 'object',
                    baseDataType: 'object' as const,
                    valueFormatter: (params: ArrayValueFormatterParams) =>
                        params.value == null ? '' : params.value.join(','),
                    valueParser: (params: ArrayValueFormatterParams) => {
                        const value = (params.newValue ?? '')
                            .split(/[ ,]+/)
                            .map((v: string) => v.trim());
                        return value;
                    },
                    dataTypeMatcher: (value: unknown): boolean =>
                        Array.isArray(value) && value !== null,
                },
            };
        }, []);

        const colDefs = useMemo<ColDefOrGroupDef[]>(() => {
            return [
                {
                    headerName: 'Ref ID',
                    field: 'refId',
                    width: 120,
                    wrapText: true,
                    cellRenderer: GridCellIframeContainer,
                    sort: 'asc',
                    pinned: 'left',
                    valueGetter: (p) =>
                        `${p.data.refId} ${p.data.archived ? 'descoped' : ''}`,
                },
                {
                    field: 'title',
                    wrapText: true,
                    autoHeight: true,
                    width: 250,
                    editable: true,
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        maxLength: 5000,
                    },
                    cellStyle: { 'white-space': 'pre-wrap' },
                },
                {
                    headerName: 'Section',
                    field: 'section',
                    width: 150,
                    editable: true,
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: {
                        values: SECTION_OPTIONS.sort(),
                    },
                },
                {
                    headerName: 'Control Description',
                    field: 'controlDescription',
                    wrapText: true,
                    autoHeight: true,
                    width: 250,
                    editable: true,
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        maxLength: 5000,
                    },
                    cellStyle: { 'white-space': 'pre-wrap' },
                },
                {
                    headerName: 'Importance',
                    field: 'importance',
                    cellRenderer: GridCellBadge,
                    width: 120,
                    editable: true,
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: {
                        values: ['Low', 'Moderate', 'High', 'Must-have'],
                    },
                },
                {
                    headerName: 'Frequency',
                    field: 'frequency',
                    cellRenderer: GridCellBadge,
                    width: 120,
                    editable: true,
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: {
                        values: [
                            'Continuous',
                            'New hires',
                            'Annual',
                            'Employees',
                            'New customers',
                            'Material vendors',
                            'Terminated employees',
                            'Quarterly',
                            'Identified vulnerabilities',
                            'Incidents',
                            'Change releases',
                        ],
                    },
                },
                {
                    headerName: 'Board Status',
                    field: 'status',
                    cellRenderer: GridCellBadge,
                    cellRendererParams: {
                        colorMapping: testingStatusColorMapping,
                    },
                    editable: true,
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: {
                        values: Object.values(cardStatusMap),
                    },
                },
                {
                    headerName: 'Attachments',
                    field: 'attachments',
                    cellRenderer: GridCellAttachments,
                    cellRendererParams: {
                        prop: 'attachments',
                    },
                    width: 250,
                },
                {
                    headerName: 'Testing Status',
                    field: 'worksheetStatus',
                    cellRenderer: GridCellBadge,
                    cellRendererParams: {
                        colorMapping: badgeTestStatusColorMapping,
                    },
                    editable: true,
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: {
                        values: Object.values(worksheetStatusMap),
                    },
                },
                {
                    headerName: 'Service Auditor Tests',
                    field: 'auditProcedure',
                    editable: true,
                    wrapText: true,
                    autoHeight: true,
                    width: 250,
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        maxLength: 5000,
                    },
                    cellStyle: { 'white-space': 'pre-wrap' },
                },
                {
                    headerName: 'Results',
                    field: 'results.details.tests',
                    cellRenderer: GridCellResult,
                    cellRendererParams: {
                        prop: 'results',
                    },
                    wrapText: true,
                    width: 250,
                    editable: (params) =>
                        params?.data?.results?.status !== 'No exceptions noted',
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        maxLength: 5000,
                    },
                    cellStyle: { 'white-space': 'pre-wrap' },
                    valueGetter: (params) => params?.data?.results?.details?.tests,
                    filterValueGetter: (params) => params?.data?.results?.status,
                },
                {
                    headerName: 'Test Results',
                    field: 'testingComments',
                    wrapText: true,
                    autoHeight: true,
                    width: 250,
                    editable: true,
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        maxLength: 5000,
                    },
                    cellStyle: { 'white-space': 'pre-wrap' },
                },
                ...generateStandardColumns(audit?.auditType || []),
                {
                    headerName: 'Senior Review',
                    valueGetter: (params: ValueGetterParams) =>
                        params.data[ReviewType.supervisor]?.status ||
                        REVIEW_STATUS.notStarted,
                    cellRenderer: GridCellReview,
                    cellRendererParams: {
                        prop: {
                            type: ReviewType.supervisor,
                        },
                    },
                    wrapText: true,
                    autoHeight: true,
                    minWidth: 220,
                    filterParams: {
                        caseSensitive: true,
                        textMatcher: ({
                            _filterOption,
                            value,
                            filterText,
                        }: {
                            _filterOption: string;
                            value: string;
                            filterText: string;
                        }): boolean => {
                            let newValue = reviewStatusStrings[value as ReviewStatus];
                            if (!newValue) newValue = value;
                            return new RegExp(filterText).test(newValue.toLowerCase());
                        },
                    },
                },
                {
                    headerName: 'Manager Review',
                    valueGetter: (params: ValueGetterParams) =>
                        params.data[ReviewType.manager]?.status ||
                        REVIEW_STATUS.notStarted,
                    cellRenderer: GridCellReview,
                    cellRendererParams: {
                        prop: {
                            type: ReviewType.manager,
                        },
                    },
                    wrapText: true,
                    autoHeight: true,
                    minWidth: 220,
                    filterParams: {
                        caseSensitive: true,
                        textMatcher: ({
                            _filterOption,
                            value,
                            filterText,
                        }: {
                            _filterOption: string;
                            value: string;
                            filterText: string;
                        }): boolean => {
                            let newValue = reviewStatusStrings[value as ReviewStatus];
                            if (!newValue) newValue = value;
                            return new RegExp(filterText).test(newValue.toLowerCase());
                        },
                    },
                },
                {
                    headerName: 'Comments',
                    field: 'uwvComments',
                    wrapText: true,
                    autoHeight: true,
                    width: 250,
                    editable: true,
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        maxLength: 5000,
                    },
                    cellStyle: { 'white-space': 'pre-wrap' },
                },
                {
                    headerName: 'Lexi Conclusion',
                    field: 'lexiConclusion',
                    width: 150,
                    cellRenderer: GridCellBadge,
                    editable: true,
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: {
                        values: Object.values(lexiConclusionsMap),
                    },
                },
                {
                    headerName: 'Lexi Output',
                    field: 'lexiOutput',
                    wrapText: true,
                    autoHeight: true,
                    width: 250,
                    editable: true,
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        maxLength: 5000,
                    },
                    cellStyle: { 'white-space': 'pre-wrap' },
                },
                {
                    headerName: 'Lexi Feedback Category',
                    field: 'lexiFeedbackCategory',
                    width: 200,
                    cellRenderer: GridCellBadge,
                    cellRendererParams: {
                        colorMapping: lexiFeedbackCategoryColorMapping,
                    },
                    editable: true,
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: {
                        values: Object.values(lexiFeedbackCategoryMap),
                    },
                },
                {
                    headerName: 'Lexi Feedback',
                    field: 'lexiFeedback',
                    wrapText: true,
                    autoHeight: true,
                    width: 250,
                    editable: true,
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        maxLength: 5000,
                    },
                    cellStyle: { 'white-space': 'pre-wrap' },
                },
                //{ headerName: 'Client Comments', field: 'clientComments' },
            ];
        }, [audit]);

        const LoadingOverlay: React.FC = () => (
            <div className="ag-overlay-loading-center">
                Please wait while your rows are loading
            </div>
        );

        const NoRowsOverlay: React.FC = () => <div>No data available to display</div>;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        function setNestedValue(obj: any, path: string, value: any) {
            const keys = path.split('.');
            let current = obj;
            for (let i = 0; i < keys.length - 1; i++) {
                if (!current[keys[i]]) {
                    current[keys[i]] = {};
                }
                current = current[keys[i]];
            }
            current[keys[keys.length - 1]] = value;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        function setAdditionalValue(value: any, col: string, card: any) {
            if (col === 'results.details.tests') {
                value.results['status'] = card?.results?.status;
            }
        }

        const handleCellEditEnd = (event: CellValueChangedEvent) => {
            console.log('handle cell edit running: ', {
                old: event.oldValue,
                new: event.newValue,
                colId: event.column.getColId(),
            });
            const oldCard = event.data as Card;
            const updatedCard: Partial<Card> = {};
            const colId = event.column.getColId();

            // Special handling for lexiConclusion - don't convert, the transformer will handle it
            if (colId === 'lexiConclusion') {
                // Store the display value directly - transformCardToApi will convert it
                setNestedValue(updatedCard, colId, event.newValue);
                console.log('lexiConclusion update:', {
                    displayValue: event.newValue,
                    updatedCard,
                });
            } else if (colId === 'lexiOutput') {
                setNestedValue(updatedCard, colId, event.newValue);
                console.log('lexiOutput update:', {
                    newValue: event.newValue,
                    updatedCard,
                });
            } else if (colId === 'lexiFeedbackCategory' || colId === 'lexiFeedback') {
                setNestedValue(updatedCard, colId, event.newValue);
                console.log(`${colId} update:`, {
                    newValue: event.newValue,
                    updatedCard,
                });
            } else {
                setNestedValue(updatedCard, colId, event.newValue);
            }

            setAdditionalValue(updatedCard, colId, oldCard);

            console.log('Sending update:', {
                cardId: oldCard.id,
                updatedCard,
            });

            // Save the card
            updateCardMutation.mutate({
                cardId: oldCard.id,
                card: updatedCard,
            });

            // Call the prop callback if provided
            if (onCellValueChangedProp) {
                onCellValueChangedProp();
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const getRowStyle = (params: any) => {
            if (params.data.archived) {
                return { background: '#f1f4ff' };
            }
        };

        const saveColumnState = () => {
            if (!gridApi.current) return;

            const columnState = gridApi.current.getColumnState();
            localStorage.setItem(
                `auditGrid-columnState-${audit?.id}`,
                JSON.stringify(columnState),
            );
        };

        const onColumnMoved = (event: ColumnMovedEvent) => {
            // Save column state after column is moved
            if (event.finished) {
                saveColumnState();
            }
        };

        const onGridReady = (params: GridReadyEvent) => {
            gridApi.current = params.api;
        };

        const onFirstDataRendered = (params: FirstDataRenderedEvent) => {
            // Restore column state from localStorage after first data is rendered
            const savedColumnState = localStorage.getItem(
                `auditGrid-columnState-${audit?.id}`,
            );
            if (savedColumnState && params.api) {
                try {
                    const columnState = JSON.parse(savedColumnState);
                    params.api.applyColumnState({
                        state: columnState,
                        applyOrder: true,
                    });
                } catch (error) {
                    console.error('Failed to restore column state:', error);
                }
            }
            // Call the prop callback if provided
            if (onFirstDataRenderedProp) {
                onFirstDataRenderedProp();
            }
        };

        // Additional effect to ensure column state is restored
        useEffect(() => {
            if (gridApi.current && audit?.id && rowData.length > 0) {
                const savedColumnState = localStorage.getItem(
                    `auditGrid-columnState-${audit.id}`,
                );
                if (savedColumnState) {
                    try {
                        const columnState = JSON.parse(savedColumnState);
                        gridApi.current.applyColumnState({
                            state: columnState,
                            applyOrder: true,
                        });
                    } catch (error) {
                        console.error(
                            'Failed to restore column state in useEffect:',
                            error,
                        );
                    }
                }
            }
        }, [gridApi.current, audit?.id, rowData.length]);

        return (
            <div className="ag-theme-quartz h-full w-full">
                <AgGridReact
                    rowData={cardsLoading ? null : rowData}
                    getRowStyle={getRowStyle}
                    columnDefs={colDefs}
                    defaultColDef={defaultColDef}
                    loadingOverlayComponent={LoadingOverlay}
                    noRowsOverlayComponent={NoRowsOverlay}
                    reactiveCustomComponents
                    dataTypeDefinitions={dataTypeDefinitions}
                    onCellValueChanged={handleCellEditEnd}
                    getRowId={(params) => params.data.id}
                    onGridReady={onGridReady}
                    onColumnMoved={onColumnMoved}
                    onFirstDataRendered={onFirstDataRendered}
                    onModelUpdated={onModelUpdated}
                />
            </div>
        );
    },
);

AuditGrid.displayName = 'AuditGrid';
export default AuditGrid;
